<template lang="pug">
  v-flex(xs12)
    v-snackbar(v-model="getTasksBulkLoading" color="success")
      div {{ tasksBulkLoadingMessage }}
    v-snackbar(v-model="getTasksError" color="error")
      div {{ tasksErrorMessage }}
    // Confirmation box on migration
    v-dialog(v-model="confirmationMigrateAll" persistent max-width="480")
      v-card
        title-bar(dark) Migrate all {{ totalItems }} tasks
        v-card-text Do you confirm to migrate all {{ totalItems }} tasks? They will be moved to the task tab where you'll be able to make it recurrent if you want.
        v-card-actions
          v-spacer
          v-btn(flat @click="confirmationMigrateAll = false") No
          v-btn.primary(@click="migrateAll()") Yes

    slide-drawer(v-model="showSlideDrawer" :confirm-dismiss="isV21")

      // Title inside the Edit / Create task
      template(
        v-if="isV21"
        slot="title"
      )
        span {{ editTaskId ? 'Edit Task' : 'Create New Task' }}
      template(
        v-else
        slot="title"
      )
        span Read only task
      template(slot="content")
        edit-task(
          ref="taskEditor"
          :id="editTaskId"
          :contact-ids="contactIds"
          :query-params="queryParamObject"
          :active-tab="activeTab"
          @input="confirmDismiss = true;"
          @changes-saved="closeSlideDrawer($event)"
        )

      // Buttons on Edit / Create Task
      template(
        v-if="!isV21"
        slot="actions"
      )
        v-btn.primary(@click.stop="$refs.taskEditor.migrateTask()")
          | Migrate task
      template(
        v-if="isV21"
        slot="actions"
      )
        v-btn.primary(
          @click.stop="$refs.taskEditor.saveTasks()"
          :disabled="buttonDisable"
        )
          | {{ editTaskId ? 'Update Task' : 'Create Task' }}

    // Task Page internal header
    action-title-bar(
      :description="badgeText"
      :is-inline="isInline"
      :hide-subtitle="isProfileV2"
      :title="isProfileV2 ? '' : 'Tasks'"
      data-cy="tasks-title-bar"
    )
      v-btn.ml-0.primary(
        v-if="activeTab === TAB_V21"
        @click="showTaskEditor()"
        :class="createTaskClass"
        :small="isProfileV2"
      ) Create Task

      // Status Filter
      v-menu(offset-y v-show="selected.length === 0" data-cy="task-filter")
        v-toolbar-title(slot="activator" :class="taskMenuClass")
          span {{ statusFilterName }}
          v-icon(color="primary" right) arrow_drop_down
        v-list
          v-list-tile(
            v-for="statusMenuItem in statusMenuItems"
            :key="statusMenuItem.value"
            @click="filterResultsByStatus(statusMenuItem.value)"
          )
            v-list-tile-title(data-cy="task-filter-item") {{statusMenuItem.text}}

      // Assigned user Filter
      template(slot="menu")
        v-menu(
          offset-y
          v-show="!hasContactIds"
          max-height="500"
        )
          v-toolbar-title.liondesk(dense slot="activator" @click="findTeamMembersIfEmpty()" :class="{'resize-task-menu': isDashboard}")
            span {{ teamMemberFilterName }}
            v-icon(color="primary" right) arrow_drop_down
          v-list
            v-list-tile(v-if="teamMembersInProgress && !this.teamMembers")
              v-list-tile-title Loading...
            template(v-else)
              v-list-tile(
                v-for="teamMember in teamMembers"
                :key="teamMember.value"
                @click="filterResultsByTeamMember(teamMember.value)"
              )
                v-list-tile-title {{teamMember.text}}

      template(slot="actions")
        v-toolbar(dense flat color="transparent")
          span(v-show="selected.length > 0")
            v-tooltip(bottom lazy)
              span Mark {{ targetTaskStatus }}
              v-btn(
                icon slot="activator"
                @click.stop="handleTaskStatusChange('multiple', null, false )"
              )
                v-icon(data-cy="bulk-task-status-icon") {{ editTaskStatusIcon }}
            v-tooltip(bottom lazy)
              v-btn(
                icon slot="activator"
                @click.stop="handleRemoveResourceModal('delete', 'multiple')"
              )
                v-icon delete
              span Delete
            v-tooltip(
              v-if="activeTab === TAB_V1"
              bottom
              lazy
            )
              v-btn(
                icon slot="activator"
                @click.stop="handleTaskMigration('multiple', null, false )"
              )
                v-icon transfer_within_a_station
              span Migrate Task

          // todo cc - comment out til post MVP
          // v-menu(offset-y)
          //   v-btn(icon slot="activator")
          //     v-icon more_vert
          //   v-list
          //     v-list-tile(@click="")
          //       edit-task
          //         v-list-tile-title(slot="activator") Create New Task
          v-btn.ml-0.primary(
            v-if="activeTab === TAB_V1 && totalItems > 0"
            @click="confirmationMigrateAll = true"
            class="no-opacity"
          ) Migrate all {{ totalItems }} tasks

    v-card
      v-container.pa-0(fluid)
        v-tabs(slider-color="primary")
          v-tab(:key="TAB_V21" @click="changeActiveTab(TAB_V21)")

            v-tooltip(right lazy)
              span
                | Migrate your existing tasks to this new task management view
                br
                | for upgraded functionality including recurring tasks, calendar
                br
                | view, editing and more.
              span(slot="activator")
                span Tasks
                span.tab
                  v-icon(small) help
          v-tab(:key="TAB_V1" @click="changeActiveTab(TAB_V1)")

            v-tooltip(right lazy)
              span
                | This task management view is read only. Migrate your tasks to
                br
                | the new task management view for upgraded functionality
                br
                | including recurring tasks, calendar view, editing and more.
              span(slot="activator")
                span Tasks
                 span.legacy (Legacy)
                span.tab
                  v-icon(small) help


          v-tab-item(:key="TAB_V21")
            v-flex(xs12)
              v-btn(icon slot="activator" @click="view = 'list'")
                v-icon list
              v-btn(icon slot="activator" @click="view = 'calendar'")
                v-icon calendar_today

            template(v-if="view === 'calendar'")
              v-container(fluid)
                calendar-view(
                  :tasks="tasks"
                  @eventClicked="showTaskEditor"
                )
            template(v-if="view === 'list'")
              v-data-table.lonewolf-table.tasks-table(
                v-if="activeTab===TAB_V21"
                ref="tasksTableV21"
                data-cy="tasks-table"
                :headers="headers"
                :items="virtualScrollVisibleItems"
                :total-items="totalItems"
                @update:pagination="sortResults"
                v-model="selected"
                item-key="id"
                :loading="inProgress"
                :class="{ 'in-progress': inProgress, 'resize-dashboard': isDashboard }"
                sort-icon="expand_less"
                must-sort
                disable-initial-sort
                hide-actions
                :select-all='true'
              )
                template(slot="items" slot-scope="props")
                  tr(v-if="props.index === 0 && virtualScrollStart > 0")
                    td.skeleton-loader(
                      v-for="index in headers.length + 1"
                      :style="'height:'+virtualScrollStartHeight+'px'")
                  tr(
                    data-cy="task-table-row"
                    :active="props.selected"
                    :class="setTableRowClass(props.expanded, props.item.description, props.item.notes)"
                  )
                    td.fixed
                      v-checkbox(data-cy="task-checkbox" v-model="props.selected" primary hide-details)
                    td
                      v-tooltip(bottom lazy v-if="props.item.is_campaign_task")
                        v-icon.mr-1(slot="activator") flight_takeoff
                        span Campaign Task
                      v-tooltip(bottom lazy v-if="props.item.is_nylas_task")
                        v-icon.mr-1(slot="activator") compare_arrows
                        span Task synced from Google
                      v-tooltip(bottom lazy v-else)
                        v-icon.mr-1(slot="activator") date_range
                        span Calendar Task
                      v-tooltip(bottom lazy v-if="props.item.isRecurrent")
                        v-icon.mr-1(slot="activator") sync
                        span Recurring
                    td.nowrap {{ props.item.due_at | formatShortDate }}
                    td.nowrap(
                      v-if="userIsActive(props.item.assigned_user) && userIsOnTeam(props.item.assigned_user)"
                    )
                      v-tooltip(bottom lazy v-if="isDashboard")
                        span(slot="activator")
                          | {{ determineAssignedUser(props.item) }}
                        span {{ getAssignedFullNameUser(props.item) }}
                      span(v-else) {{ determineAssignedUser(props.item) }}
                    td.nowrap(v-else-if="!userIsActive(props.item.assigned_user)")
                      v-tooltip(bottom lazy)
                        span(slot="activator").line-through-user
                          | {{ determineAssignedUser(props.item) }}
                        span {{ deletedUserDisclaimer }}
                    td.nowrap(v-else-if="!userIsOnTeam(props.item.assigned_user)")
                      v-tooltip(bottom lazy)
                        span(slot="activator").line-through-user
                          | {{ determineAssignedUser(props.item) }}
                        span {{ userNotOnTeamDisclaimer }}
                    td.nowrap.toggle(
                      @click="props.expanded = !props.expanded"
                      data-cy="task-description"
                    )
                      .text-truncate
                        | {{ trimLength(props.item.description) }}
                      .grey--text(v-if="props.item.notes !== null")
                        | {{ trimLength(props.item.notes) }}
                      v-icon(v-if="isRowExpandable(props.item.description, props.item.notes)") expand_more
                    td.nowrap(v-if="!hasContactIds")
                      router-link(
                        v-if="hasContact(props.item) && !contactIsSoftDeleted(props.item.contact)"
                        :to="{ name: 'contact-profile', params: { id: props.item.contact_id }}")
                          | {{ props.item.contact_name | replaceBlankName }}
                      contact-menu(
                          v-if="hasContact(props.item) && !contactIsSoftDeleted(props.item.contact)"
                        :contact="props.item"
                      )
                      v-flex(v-else-if="hasContact(props.item) && contactIsSoftDeleted(props.item.contact)")
                        v-tooltip(bottom lazy)
                          span(slot="activator").line-through-user
                            | {{ props.item.contact_name | replaceBlankName }}
                          span {{ deletedContactDisclaimer }}
                    td
                      v-chip(
                        label
                        small
                        text-color="white"
                        v-if="props.item.category && props.item.category.name && props.item.category.name !== 'None'"
                        :color="props.item.category.color"
                      )
                        | {{ props.item.category.name }}
                    td.text-xs-right.actions(data-cy="task-action-icons")
                      .single-row
                        v-tooltip(bottom lazy)
                          span Edit Task
                          v-btn(icon slot="activator" @click="showTaskEditor(props.item)")
                            v-icon edit
                        v-tooltip(bottom lazy)
                          span Mark {{ targetTaskStatus }}
                          v-btn(
                            icon slot="activator"
                            @click="handleTaskStatusChange('single', props.item, false)"
                          )
                            v-icon(data-cy="task-status-icon") {{ editTaskStatusIcon }}
                        v-tooltip(bottom lazy)
                          span Delete Task
                          v-btn(
                            icon slot="activator"
                            @click="handleRemoveResourceModal('delete', 'single', props.item)"
                          )
                            v-icon delete
                      // todo cc - comment out til post MVP
                      // v-btn(icon)
                      //   v-icon more_vert
                  tr(v-if="props.index === virtualScrollVisibleItems.length -1 && virtualScrollStart + virtualScrollPerPage < tasks.length")
                    td.skeleton-loader(
                      v-for="index in headers.length + 1"
                      :style="'height:'+virtualScrollEndHeight+'px'")
                template(slot="expand" slot-scope="props")
                  div.pa-4(
                    v-if="isRowExpandable(props.item.description, props.item.notes)"
                  )
                    .font-weight-bold {{ props.item.description }}
                    span(v-if="props.item.notes !== null")
                      | {{ props.item.notes }}
          v-tab-item(:key="TAB_V1")
            v-data-table.lonewolf-table.tasks-table(
              v-if="activeTab===TAB_V1"
              ref="tasksTableV1"
              data-cy="tasks-table"
              :headers="headers"
              :items="virtualScrollVisibleItems"
              :total-items="totalItems"
              @update:pagination="sortResults"
              v-model="selected"
              item-key="id"
              :loading="inProgress"
              :class="{ 'in-progress': inProgress, 'resize-dashboard-v1': isDashboard }"
              sort-icon="expand_less"
              disable-initial-sort
              hide-actions
              :select-all='true'
            )
              template(slot="items" slot-scope="props")
                tr(v-if="props.index === 0 && virtualScrollStart > 0")
                  td.skeleton-loader(
                    v-for="index in headers.length + 1"
                    :style="'height:'+virtualScrollStartHeight+'px'")
                tr(
                  data-cy="task-table-row"
                  :active="props.selected"
                  :class="setTableRowClass(props.expanded, props.item.description, props.item.notes)"
                )
                  td.fixed
                    v-checkbox(data-cy="task-checkbox" v-model="props.selected" primary hide-details)
                  td
                    v-tooltip(bottom lazy v-if="props.item.is_campaign_task")
                      v-icon.mr-1(slot="activator") flight_takeoff
                      span Campaign Task
                    v-tooltip(bottom lazy v-else)
                      v-icon.mr-1(slot="activator") date_range
                      span Calendar Task
                    v-tooltip(bottom lazy v-if="props.item.isRecurrent")
                      v-icon.mr-1(slot="activator") sync
                      span Recurring
                    span(v-if="props.item.crm_task_id") Migrated
                  td.nowrap {{ props.item.due_at | formatShortDate }}
                  td.nowrap(
                    v-if="userIsActive(props.item.assigned_user) && userIsOnTeam(props.item.assigned_user)"
                  )
                    | {{ determineAssignedUser(props.item) }}
                  td.nowrap(v-else-if="!userIsActive(props.item.assigned_user)")
                    v-tooltip(bottom lazy)
                      span(slot="activator").line-through-user
                        | {{ determineAssignedUser(props.item) }}
                      span {{ deletedUserDisclaimer }}
                  td.nowrap(v-else-if="!userIsOnTeam(props.item.assigned_user)")
                    v-tooltip(bottom lazy)
                      span(slot="activator").line-through-user
                        | {{ determineAssignedUser(props.item) }}
                      span {{ userNotOnTeamDisclaimer }}
                  td.nowrap.toggle(
                    @click="props.expanded = !props.expanded"
                    data-cy="task-description"
                  )
                    .text-truncate
                      | {{ trimLength(props.item.description) }}
                    .grey--text(v-if="props.item.notes !== null")
                      | {{ trimLength(props.item.notes) }}
                    v-icon(v-if="isRowExpandable(props.item.description, props.item.notes)") expand_more
                  td.nowrap(v-if="!hasContactIds")
                    router-link(
                        v-if="hasContact(props.item) && !contactIsSoftDeleted(props.item.contact)"
                        :to="{ name: 'contact-profile', params: { id: props.item.contact_id }}")
                          | {{ props.item.contact_name | replaceBlankName }}
                    contact-menu(
                        v-if="hasContact(props.item) && !contactIsSoftDeleted(props.item.contact)"
                      :contact="props.item"
                    )
                    v-flex(v-else-if="hasContact(props.item) && contactIsSoftDeleted(props.item.contact)")
                      v-tooltip(bottom lazy)
                        span(slot="activator").line-through-user
                          | {{ props.item.contact_name | replaceBlankName }}
                        span {{ deletedContactDisclaimer }}
                  td
                    v-chip(
                      label
                      small
                      text-color="white"
                      v-if="props.item.category && props.item.category.name && props.item.category.name !== 'None'"
                      :color="props.item.category.color"
                    )
                      | {{ props.item.category.name }}
                  td.text-xs-right.actions(data-cy="task-action-icons")
                    .single-row
                      v-tooltip(bottom lazy)
                        span Edit Task
                        v-btn(icon slot="activator" @click="showTaskEditor(props.item)")
                          v-icon edit
                      v-tooltip(bottom lazy)
                        span Mark {{ targetTaskStatus }}
                        v-btn(
                          icon slot="activator"
                          @click="handleTaskStatusChange('single', props.item, false)"
                        )
                          v-icon(data-cy="task-status-icon") {{ editTaskStatusIcon }}
                      v-tooltip(bottom lazy)
                        span Delete Task
                        v-btn(
                          icon slot="activator"
                          @click="handleRemoveResourceModal('delete', 'single', props.item)"
                        )
                          v-icon delete
                      v-tooltip(
                        v-if="!props.item.crm_task_id"
                        bottom
                        lazy
                      )
                        span Migrate Task
                        v-btn(icon slot="activator" @click="handleTaskMigration('single', props.item, false)")
                          v-icon transfer_within_a_station
                    // todo cc - comment out til post MVP
                    // v-btn(icon)
                    //   v-icon more_vert
                tr(v-if="props.index === virtualScrollVisibleItems.length -1 && virtualScrollStart + virtualScrollPerPage < tasks.length")
                  td.skeleton-loader(
                    v-for="index in headers.length + 1"
                    :style="'height:'+virtualScrollEndHeight+'px'")
              template(slot="expand" slot-scope="props")
                div.pa-4(
                  v-if="isRowExpandable(props.item.description, props.item.notes)"
                )
                  .font-weight-bold {{ props.item.description }}
                  span(v-if="props.item.notes !== null")
                    | {{ props.item.notes }}
      
      v-layout.pagination
        v-flex.pages
            v-pagination(
              v-if="paginationPageCount(totalItems, tasksPageSize) > 1"
              v-model="paginationPage"
              :length="paginationPageCount(totalItems, tasksPageSize)"
              :total-visible="7"
            )
        v-flex.limits
          pagination-limit.mr-4(
            :limits="paginationLimits"
            :current-limit="tasksPageSize"
            @change:limit="loadWithNewPageLimit($event, 'tasksPageSize')"
          )
      remove-resource-modal(
        v-model="removeTaskDialog"
        :action="removeAction"
        resource-singular="task"
        resource-plural="tasks"
        :isV21="activeTab === TAB_V21"
        :remove-amount="removeAmount"
        @close="onRemoveResourceModalClose"
        @setDeleteType="onSetDeleteType"
        :itemTask="handledTask"
      )
      v-snackbar(
        v-model="statusSnackbar"
        color="success"
        ) {{ changedStatusMessage }}
        v-btn(
          flat
          @click="handleTaskStatusChange(editStatusAmount, lastTask, true)"
        ) Undo
      v-snackbar(
        v-model="migrationSnackbar"
        color="success"
        ) {{ migrationMessage }}
</template>

<script>

import { createNamespacedHelpers, mapActions, mapMutations } from 'vuex';
import { formatShortDate } from '@/Core/filters/dates';
import replaceBlankName from '@/Core/filters/replaceBlankName';
import Pagination from '@/Core/mixins/pagination';
import ShowItemTotals from '@/Core/mixins/show-item-totals';
import VirtualScroll from '@/Core/mixins/virtual-scroll';
import PaginationLimit from '@/Core/components/PaginationLimit';
import toggleSlideDrawer from '@/Core/mixins/toggle-slide-drawer';
import ActionTitleBar from '@/Core/components/ActionTitleBar';
import SlideDrawer from '@/Core/components/SlideDrawer';
import getFullName from '@/Core/filters/getFullName';
import getInitials from '@/Core/filters/getInitials';
import TitleBar from '@/Core/components/TitleBar';
import ContactMenu from './ContactMenu';
import EditTask from './EditTask';
import CalendarView from './CalendarView';
import RemoveResourceModal from './RemoveResourceModal';
import taskTableHeaders from '../config/table-headers';
import { CONTACT_ASSIGNED, TAB_V1, TAB_V21 } from '../config/constants';

const { mapState: mapTasksState, mapActions: mapTasksActions } = createNamespacedHelpers('tasks');
const { mapState: mapPreferencesState } = createNamespacedHelpers('preferences');
const { mapState: mapUserInformationState } = createNamespacedHelpers('userInformation');
const {
  mapGetters: mapTeamMembersGetters,
  mapActions: mapTeamMembersActions,
  mapState: mapTeamMembersState,
} = createNamespacedHelpers('teamMembers');
const { mapState: mapBulkActionsState } = createNamespacedHelpers('contactsTable/bulkActions');

export default {
  components: {
    TitleBar,
    ActionTitleBar,
    EditTask,
    RemoveResourceModal,
    SlideDrawer,
    PaginationLimit,
    ContactMenu,
    CalendarView,
  },
  mixins: [
    toggleSlideDrawer,
    Pagination,
    ShowItemTotals,
    VirtualScroll,
  ],
  filters: {
    formatShortDate,
    replaceBlankName,
  },

  props: {
    page: {
      type: Number,
      default: 1,
    },
    statusFilter: {
      type: String,
      default: 'current',
    },
    teamMemberFilter: {
      type: String,
      default: '',
    },
    sortOrder: {
      type: String,
      default: 'asc',
    },
    sortField: {
      type: String,
      default: 'due_at',
    },
    isPreviewMode: {
      type: Boolean,
      default: false,
    },
    contactIds: {
      type: Array,
      default: () => [],
    },
    maxDescriptionLength: {
      type: Number,
      default: 100,
    },
    isDashboard: {
      type: Boolean,
      default: false,
    },
    isProfileV2: {
      type: Boolean,
      default: false,
    }
  },

  data() {
    return {
      selected: [],
      statusMenuItems: [
        { value: 'current', text: 'Current' }, // all tasks w/o due date or due in the future
        { value: 'upcoming', text: 'All Upcoming' }, // all tasks due in the future
        { value: 'overdue', text: 'Overdue' }, // all past due tasks
        { value: 'complete', text: 'Complete' }, // complete
      ],
      TAB_V1: TAB_V1,
      TAB_V21: TAB_V21,
      updateSort: false,
      editTaskId: null,
      removeTaskId: null,
      removeTaskDialog: false,
      isDeletingTask: false,
      isCompletingTask: false,
      removeAction: 'delete',
      removeAmount: 'single',
      internalStatusFilter: this.statusFilter,
      internalTeamMemberFilter: this.teamMemberFilter,
      internalSortOrder: this.sortOrder,
      internalSortField: this.sortField,
      deletedUserDisclaimer: 'This user does not have an active subscription',
      userNotOnTeamDisclaimer: 'This user is no longer a teammate',
      lastTasksSelected: [],
      statusSnackbar: false,
      migrationSnackbar: false,
      lastTask: null,
      taskStatus: null,
      editStatusAmount: null,
      currentTable: 'tasks',
      isInline: true,
      confirmationMigrateAll: false,
      isV21: false,
      deleteType: 0,
      view: "list",
      handledTask: null,
      editTaskMessage: '',
      isEditTaskSuccessResponse: true,
      isEditTaskMessage: false,
      deletedContactDisclaimer: 'This contact was deleted. Their profile is not accessible.',
      shouldDisableBtn: false,
      height: "300px",
    };
  },

  computed: {
    badgeText() {
      const numSelected = this.selected.length;
      return this.showItemTotals_show(numSelected, this.totalItems, 'Task', 'Tasks');
    },
    ...mapPreferencesState(['tasksPageSize']),
    ...mapUserInformationState(['user']),
    ...mapTasksState([
      'activeTab',
      'tasks',
      'totalItems',
      'pages',
      'inProgress',
      'savingRecurringTask',
      'tasksBulkLoading',
      'tasksBulkLoadingMessage',
      'tasksError',
      'tasksErrorMessage',
      'buttonDisable',
    ]),
    ...mapBulkActionsState(['startCampaignSuccess']),
    ...mapTeamMembersState(['teamMembersInProgress']),
    ...mapTeamMembersGetters(['teamMembersInfo']),
    /**
     * If currently showing completed tasks, can only change the status to Incomplete,
     * and vice versa
     */
    isEditingSingleTaskStatus() {
      return this.editStatusAmount === 'single';
    },
    changedStatusMessage() {
      return this.editStatusAmount === 'single' ? 'Task status changed successfully' : 'Tasks status changed successfully';
    },
    migrationMessage() {
      return this.editStatusAmount === 'single' ? 'Task migrated successfully' : 'Tasks migrated successfully';
    },
    targetTaskStatus() {
      return this.isShowingCompletedTasks ? 'incomplete' : 'complete';
    },
    editTaskStatusIcon() {
      return this.isShowingCompletedTasks ? 'restore' : 'check_circle';
    },
    isShowingCompletedTasks() {
      return this.internalStatusFilter === 'complete';
    },
    undoTaskStatus() {
      return this.isShowingCompletedTasks ? 'complete' : 'incomplete';
    },
    hasContactIds() {
      return this.contactIds && this.contactIds.length > 0;
    },
    headers() {
      let headers = taskTableHeaders.filter(header => {
        return header.isPreview;
      });

      if (this.hasContactIds) {
        headers = taskTableHeaders.filter(header => {
          return header.isContactFiltered;
        });
      }

      return headers;
    },
    queryParamObject() {
      
      return {
        page: this.paginationPage,
        statusFilter: this.internalStatusFilter,
        teamMemberFilter: this.internalTeamMemberFilter,
        sortOrder: this.internalSortOrder,
        sortField: this.internalSortField,
        contactIds: this.contactIds,
      };
    },
    isDeletingSingleTask() {
      return this.removeAction === 'delete' && this.removeAmount === 'single';
    },
    isDeletingMultipleTasks() {
      return this.removeAction === 'delete' && this.removeAmount === 'multiple';
    },
    statusFilterName() {
      return this.statusMenuItems.find(f => {
        return f.value === this.internalStatusFilter;
      }).text;
    },
    teamMemberFilterName() {
      if (this.teamMembers.length > 0) {
        const teamMember = this.teamMembers.find(f => {
          return f.value === parseInt(this.internalTeamMemberFilter);
        });
        if (!teamMember) {
          return 'My Tasks';
        }
        return teamMember.text;
      }
      return 'My Tasks';
    },
    teamMembers() {
      const teamMembersInfo = this.teamMembersInfo.map(teamMember => ({ ...teamMember }));

      teamMembersInfo.forEach(teamMember => {
        if (this.user && teamMember.value === this.user.id) {
          teamMember.text = 'My Tasks';
        }
      });

      const teamMembers = teamMembersInfo.filter((teamMember, index) => {
        return index === teamMembersInfo.findIndex(obj => {
          return JSON.stringify(obj) === JSON.stringify(teamMember);
        });
      });

      return teamMembers;
    },
    getTasksError: {
      get() {
        return this.tasksError;
      },
      set(isError) {
        this.resetTaskLoadingState();
      },
    },
    orderFilterWrapper() {
      const statusFiltersOrderAsc = ['current', 'upcoming'];
      return statusFiltersOrderAsc.includes(this.internalStatusFilter) ? 'asc' : this.sortOrder;
    },
    taskMenuClass() {
      if (this.isDashboard) return 'resize-task-menu liondesk';
      if (this.isProfileV2) return 'menu-v2 pb-2';
      return 'liondesk';
    },
    createTaskClass() {
      if (this.isDashboard) return 'resize-task-btn';
      if (this.isProfileV2) return 'primaryv2 mr-3';
      return;
    },
    getTasksBulkLoading: {
      get() {
        return this.tasksBulkLoading;
      },
      set(value) {
        this.resetTaskLoadingState();
      }
    },
    tableContainer() {
      const table = this.activeTab === TAB_V21 ? this.$refs.tasksTableV21 : this.$refs.tasksTableV1;
      if (!table) return;
      return table.$el.querySelector('.v-table__overflow');
    },
  },

  watch: {
    paginationPage(value) {
      this.setPage();
      this.findTasksWhileLoading({
        ...this.queryParamObject,
        page: value,
      });
      this.selected = [];

      if (!(this.isPreviewMode || this.hasContactIds)) {
        this.routeChange(
          value,
          this.statusFilter,
          this.teamMemberFilter,
          this.sortOrder,
          this.sortField,
        );
      }
    },
    showSlideDrawer(value) {
      if (!value) {
        this.resetTask()
      }
      this.setIsEditTaskOpen(value);
    },
    tasksPageSize(value) {
      this.findTasksWhileLoading(this.queryParamObject);
      this.selected = [];
    },
    sortOrder: {
      handler(value) {
        this.internalSortOrder = value;
      },
    },
    sortField: {
      handler(value) {
        this.internalSortField = value;
      },
    },
    statusFilter: {
      handler(value) {
        this.internalStatusFilter = value;
      },
    },
    teamMemberFilter: {
      handler(value) {
        this.internalTeamMemberFilter = value;
      },
    },
    internalSortOrder: {
      handler(value) {
        this.findTasksWhileLoading(this.queryParamObject);
        this.selected = [];
      },
    },
    internalStatusFilter: {
      handler(value) {
        this.queryParamObject.sortOrder = this.orderFilterWrapper;
        this.findTasksWhileLoading(this.queryParamObject);
        this.selected = [];
      },
    },
    internalTeamMemberFilter: {
      handler(value) {
        this.findTasksWhileLoading(this.queryParamObject);
        this.selected = [];
      },
    },
    user: {
      immediate: true,
      handler(newValue, oldValue) {
        if (oldValue === null && newValue) {
          this.internalSortField = this.sortField;
          this.queryParamObject.sortOrder = this.orderFilterWrapper;
          this.findTasksWhileLoading(this.queryParamObject);
          this.selected = [];
        }
      },
    },
    startCampaignSuccess: {
      handler(newValue) {
        if(newValue) {
          this.findTasksWhileLoading(this.queryParamObject);
        }
      }
    },
    tasks: {
      handler(val) {
        this.updateVirtualScrollerItems(val);
        this.resizeContainer();
      },
    },
  },

  created() {
    this.queryParamObject.sortOrder = this.orderFilterWrapper;
  },
  mounted() {
    if (!this.inProgress) {
      this.findTasks(this.queryParamObject);
    }
    this.initVirtualScroller();
    window.addEventListener('resize', this.resizeContainer);
  },
  async beforeDestroy() {
    this.cleanTasks();
    this.removeScrollEvent();
    window.removeEventListener('resize', this.resizeContainer);
  },

  methods: {
    ...mapTasksActions([
      'findTasks',
      'getTask',
      'setActiveTab',
      'editTaskStatus',
      'migrateTasks',
      'migrateAllTasks',
      'deleteTask',
      'findContactsSelected',
      'setInProgress',
      'cleanTasks',
      'resetTask',
      'setDeletedTask',
      'resetTaskLoadingState',
      'resetBtnStateWhenOpenDrawer',
    ]),
    ...mapTeamMembersActions(['findTeamMembersIfEmpty']),
    ...mapMutations('tasks', ['setTasksErrorManual', 'setIsEditTaskOpen']),
    async findTasksWhileLoading(queryParamObject) {
      if (!this.inProgress) {
        this.setInProgress(true);
        await this.findTasks(this.queryParamObject);
        this.setInProgress(false);
      }
    },
    async changeActiveTab(activeTab) {
      this.selected = [];
      this.removeScrollEvent();
      await this.setActiveTab(activeTab)
      await this.findTasksWhileLoading(this.queryParamObject);
      this.initVirtualScroller();
    },
    routeChange(page, statusFilter, teamMemberFilter, sortOrder, sortField) {
      this.$router.push({
        name: 'tasks',
        query: {
          p: page,
          stf: statusFilter,
          tf: (String)(teamMemberFilter),
          so: sortOrder,
          sf: sortField,
          l: this.tasksPageSize,
        },
      });
    },
    getSortOrder(descending) {
      let sortOrder;
      if (descending) sortOrder = 'desc';
      else if (descending === false) sortOrder = 'asc';
      else if (descending === null) sortOrder = null;
      return sortOrder;
    },
    sortResults(value) {
      const { descending, sortBy } = value;
      const sortOrder = this.getSortOrder(descending);
      const sortField = sortBy;
      const page = 1;
      // Not sure why we have this. Commenting it to make the sort work
      // leave it here so we can track this change
      // if (this.hasContactIds) {
        this.internalSortOrder = sortOrder;
        this.internalSortField = sortBy;
        this.paginationPage = page;
      // }
      this.findTasksWhileLoading(this.queryParamObject);
    },
    filterResultsByStatus(value) {
      if (this.isPreviewMode || this.hasContactIds) {
        this.internalStatusFilter = value;
        this.internalSortOrder = this.orderFilterWrapper;
        this.internalSortField = this.sortField;
      } else {
        // If the user selects a new filter it will set the page to 1
        // and not keep page information between different filters
        const page = 1;
        this.routeChange(
          page,
          value,
          this.teamMemberFilter,
          this.sortOrder,
          this.sortField,
        );
      }
    },
    filterResultsByTeamMember(value) {
      this.selectedTeamMember = value;
      if (this.isPreviewMode || this.hasContactIds) {
        this.internalTeamMemberFilter = value;
      } else {
        // If the user selects a new filter it will set the page to 1
        // and not keep page information between different filters
        const page = 1;
        this.routeChange(page, this.statusFilter, value, this.sortOrder, this.sortField);
      }
    },
    async migrateAll() {
      this.confirmationMigrateAll = false
      await this.migrateAllTasks(this.queryParamObject);
      this.findTasksWhileLoading(this.queryParamObject);
    },
    async showTaskEditor(item) {
      this.resetBtnStateWhenOpenDrawer();
      this.openSlideDrawer();
      if (item){
        this.editTaskId = item.id;
        this.isV21 = item.isV21
      } else {
        this.editTaskId = null;
        this.isV21 = true
      }
      await this.getTask(item);
      await this.findContactsSelected(this.contactIds);
    },
    onSetDeleteType(value) {
      this.deleteType = value
    },
    onRemoveResourceModalClose(isResourceRemoved) {
      this.removeTaskDialog = false;
      if (isResourceRemoved) {
        if (this.isDeletingSingleTask) this.deleteSingleTask(this.removeTaskId, this.deleteType);
        if (this.isDeletingMultipleTasks) this.deleteTasks();
      }
    },
    handleRemoveResourceModal(action, amount, item = null) {
      this.removeTaskDialog = true;
      this.removeAction = action;
      this.removeAmount = amount;
      if (item) {
        this.handledTask = item;
        this.removeTaskId = item.id;
      }
    },
    async handleTaskStatusChange(amount, task = {}, isUndo) {
      this.taskStatus = this.targetTaskStatus;
      if (isUndo) {
        this.taskStatus = this.undoTaskStatus;
        this.statusSnackbar = false;
        this.selected = [...this.lastTasksSelected];
      }
      this.lastTask = task;
      this.editStatusAmount = amount;
      if (this.isEditingSingleTaskStatus) {
        await this.editSingleTaskStatus(this.lastTask);
      } else {
        await this.editTaskStatuses();
      }
      if (!isUndo) this.statusSnackbar = true;
    },
    async handleTaskMigration(amount, task = {}, isUndo) {
      let ids = []

      this.editStatusAmount = amount;
      if (isUndo) {
        this.migrationSnackbar = false;
        this.selected = [...this.lastTasksSelected];
      }

      if (this.selected.length) {
        ids = this.selected.map(task => task.id)
      } else {
        ids = [task.id]
        this.lastTask = task;
      }

      await this.migrateTasks({
        ids,
        queryParams: this.queryParamObject,
      });
      await this.findTasksWhileLoading(this.queryParamObject);
      this.selected = [];

      if (!isUndo) this.migrationSnackbar = true;
    },
    async editSingleTaskStatus(task) {
      await this.editTaskStatus({
        taskChange: task,
        status: this.taskStatus,
        queryParams: this.queryParamObject,
      });
      await this.findTasksWhileLoading(this.queryParamObject);
      this.selected = [];
    },
    async deleteSingleTask(id) {
      await this.deleteTask({ id, queryParams: this.queryParamObject, deleteType: this.deleteType });
      await this.findTasksWhileLoading(this.queryParamObject);
      const TASK_AMOUNT = 1;
      this.setDeletedTask(TASK_AMOUNT);
      this.selected = [];
    },
    async editTaskStatuses() {
      this.setInProgress(true);
      // todo bk: use a bulk complete action once supported
      await Promise.all(this.selected.map(task => {
        return this.editTaskStatus({
          taskChange: task,
          status: this.taskStatus,
          queryParams: this.queryParamObject,
        });
      }));
      this.lastTasksSelected = [...this.selected];
      this.selected = [];
      await this.findTasksWhileLoading(this.queryParamObject);
      this.setInProgress(false);
    },
    async deleteTasks() {

      this.deleteType = 1

      // todo bk: use a bulk delete action once supported
      await Promise.all(this.selected.map(task => {
        return this.deleteTask({ id: task.id, queryParams: this.queryParamObject, deleteType: this.deleteType });
      }));

      this.setDeletedTask(this.selected.length);

      this.selected = [];
      await this.findTasks(this.queryParamObject);
      this.setInProgress(false);
    },
    /**
     * Determines the assigned user of the task, either directly through the task or
     * through the associated contact
     *
     * Carries the "-1" (CONTACT_ASSIGNED constant) logic forward through to this part
     * of the application since the assigned user value has special logic with it that
     * is important to how tasks are displayed
     *
     * @param  {Object} item The task
     * @return {String}      One of the following: 'Me', 'Me (Contact Assignee)',
     *                       '<Team Member Name>', '<Team Member Name> (Contact Assignee)'
     *                       '(Contact Assignee)', '(Unnamed Assignee)'
     */
    determineAssignedUser(item) {
      // If the task has an assigned user associated to it, and it's NOT assigned thru the contact
      if (typeof item.assigned_user === 'object' && item.assigned_user_id !== CONTACT_ASSIGNED) {
        // If the task is assigned to the current user
        if (item.assigned_user_id === this.user.id) {
          return 'Me';
        }
        // (else) If the task is assigned to a team member
        const fullName = getFullName(item.assigned_user);
        return this.isDashboard && fullName ? getInitials(fullName) : fullName;
      // If the task is assigned through a contact
      } else if (item.assigned_user_id === CONTACT_ASSIGNED) {
        if (item.contact && item.contact.assigned_user_id) {
          // If the task is was assigned to the current user through the associated contact
          if (item.contact.assigned_user_id === this.user.id) {
            return 'Me (Contact Assignee)';
          }
          // (else) If the task is assigned to a team member through the associated contact
          const assignee = getFullName({
            first_name: item.contact.assigned_first_name,
            last_name: item.contact.assigned_last_name,
          });
          if (assignee !== '') {
            return `${assignee} (Contact Assignee)`;
          }
          // If the team member has no name, but the task is still assigned through a contact
          // associated with them
          return '(Contact Assignee)';
        }
      }
    },
    getAssignedFullNameUser(item) {
      return getFullName(item.assigned_user);
    },
    isOverMaxLength(content) {
      return content.length > this.maxDescriptionLength;
    },
    trimLength(content) {
      if (content) {
        if (this.isOverMaxLength(content)) {
          return `${content.substring(0, this.maxDescriptionLength)}...`;
        }
        return content;
      }
      return '';
    },
    isRowExpandable(description, notes) {
      if (description && this.isOverMaxLength(description)) {
        return true;
      }
      if (notes && this.isOverMaxLength(notes)) {
        return true;
      }
      return false;
    },
    /**
     * Set the class to 'row-expandable' if the character count exceeds this.maxDescriptionLength
     * Once a user clicks on it to expand it, change the class to 'row-expanded'
     * else return no class
     * @param {Boolean} expanded if the row was toggled to be expanded
     * @param {String} description
     */
    setTableRowClass(expanded, description, notes) {
      if (this.isRowExpandable(description, notes)) {
        if (expanded) {
          return 'row-expanded';
        }
        return 'row-expandable';
      }
      return '';
    },
    userIsActive(assignedUser) {
      return assignedUser.is_active;
    },
    userIsOnTeam(assignedUser) {
      return assignedUser.is_on_team;
    },
    contactIsSoftDeleted(contact) {
      return contact.idStatus === 0 || contact.is_active === 0;
    },
    hasContact(task) {
      return task.contact_id && task.contact;
    },
    setPage() {
      const table = this.activeTab === TAB_V21 ? this.$refs.tasksTableV21 : this.$refs.tasksTableV1;
      this.pagination_scrollToTop(table);
    },
    async initVirtualScroller() {
      let virtualScrollOptions = {
        items: this.tasks,
        container : this.tableContainer,
        rowHeight: 48,
        buffer: 10,
        minPageSize: 40,
      }

      await this.initializeVirtualScroller(virtualScrollOptions)
      this.tableContainer.addEventListener('scroll', this.onVirtualScroll);
      this.resizeContainer();
    },
    resizeContainer() {
      if (this.tableContainer) {
        this.updateVirtualScrollerContainerHeight(this.tableContainer.offsetHeight);
      }
      this.onVirtualScroll();
    },
    removeScrollEvent() {
      if (!this.container) return;
      this.container.removeEventListener('scroll', this.onVirtualScroll);
    },
  },
};
</script>

<style lang="scss" scoped>
  .legacy {
    font-size: 10px;
    margin-top: 2px;
    margin-left: 2px;
  }
  .tab{
    margin-top: -10px;
    position: absolute;
  }
  .no-opacity {
    opacity: 1;
  }
  .v-icon.outlined {
    border: 1px solid currentColor;
    border-radius: 10%;
    height: 25px;
    width: 25px;
  }
  .row-expanded {
    .toggle {
      .v-icon {
        transform: rotate(-180deg);
      }
    }
  }
  .row-expandable, .row-expanded {
    .toggle {
      cursor: pointer;
      .v-icon {
        margin-left: 6px;
        font-size: 18px;
        line-height: 2;
      }
    }
  }
  .line-through-user {
    text-decoration: line-through;
  }
  .contact-menu-actions >{
    vertical-align: top;
  }

  .tasks-table {
    & /deep/ .v-table__overflow td {
      height: auto;
      padding-top: 0;
      padding-bottom: 0;
    }

    &.resize-dashboard /deep/ .v-table__overflow {
      height: 304px;
    }

    &.resize-dashboard-v1 /deep/ .v-table__overflow {
      height: 352px;
    }
  }

  @media screen and (min-width : 1481px) and (max-width: 1685px) {
    .resize-task-menu, .resize-task-btn {
      width: 130px;
    }
  }

  @media screen and (min-width : 1263px) and (max-width: 1480px) {
    .resize-task-menu {
      width: 80px;
    }
    .resize-task-btn {
      width: 120px;
    }
  }

  .menu-v2 {
    color: var(--v-primaryv2-base);
    font-size: 14px;
    font-weight: bold;
  }

  .profile-v2-btn {
    font-weight: normal;
  }

  .skeleton-loader {
    background: url("/static/img/cell-skeleton-loader.svg"), repeating-linear-gradient(#fdfdfd, #fdfdfd 47px, #dddddd 48px);
    background-size: 100% 48px, auto;
  }
</style>
